import { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { checkIsAdminUser } from './cognitoUserGroups';

export * from './cognitoUserAttributes';
export * from './cognitoUserGroups';
export * from './createAccount';
export * from './emailVerification';
export * from './resendVerificationEmail';

export const sendForgotPasswordEmail = async (email: string) => {
  try {
    return await Auth.forgotPassword(email.toLowerCase());
  } catch (ex: any) {
    return { error: ex.message };
  }
};

export const submitForgotPassword = async (
  ...params: Parameters<typeof Auth.forgotPasswordSubmit>
): Promise<
  { result: string; error: undefined } | { result: undefined; error: string }
> => {
  try {
    const result = await Auth.forgotPasswordSubmit(...params);

    return { result, error: undefined };
  } catch (ex: any) {
    return { error: ex.message, result: undefined };
  }
};

/** Gets the currently authenticated user */
export const getCurrentAuthUser = async (
  ...params: Parameters<typeof Auth.currentAuthenticatedUser>
) => {
  try {
    return (await Auth.currentAuthenticatedUser(...params)) as CognitoUser;
  } catch (ex) {
    return;
  }
};

/** Signs in a user */
export const signIn = async (...params: Parameters<typeof Auth.signIn>) =>
  await Auth.signIn(...params);

export const signOut = async (...params: Parameters<typeof Auth.signOut>) =>
  await Auth.signOut(...params);

export const completeNewPassword = async (
  ...params: Parameters<typeof Auth.completeNewPassword>
) => await Auth.completeNewPassword(...params);

/** Even though CognitoUser has no 'username' attribute in its type, this is the easiest way to get the username */
export const getCognitoUserUsername = (
  user?: CognitoUser
): string | undefined => (user ? (user as any).username : undefined);

export const checkIsAkinUser = (user?: CognitoUser) =>
  getCognitoUserUsername(user)?.endsWith('@akin.com') ||
  checkIsAdminUser(user) ||
  false;

export const currentUserIs = async (username: string) => {
  const current_user = await getCurrentAuthUser();
  return getCognitoUserUsername(current_user) === username;
};

export const currentUserIncludes = async (usernames: string[]) => {
  const current_user = await getCurrentAuthUser();
  const username = getCognitoUserUsername(current_user);
  return username ? usernames.includes(username) : false;
};

export async function getCognitoAccessToken() {
  return (await Auth.currentSession()).getIdToken().getJwtToken();
}

export async function getAuthorizationHeader() {
  return { Authorization: `Bearer ${await getCognitoAccessToken()}` };
}

export async function getAuthorizedAPIOptions(overrides?: any) {
  const { headers, ...rest } = overrides ?? {};

  return {
    headers: {
      ...(await getAuthorizationHeader()),
      ...headers,
    },
    ...rest,
  };
}
